import React from 'react';
import { capitalize, get, memoize, toNumber, uniqBy, upperFirst } from 'lodash';
import { lowerCase } from 'lodash/fp';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { Text } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { Adjustment, Refund, BillingDocWithLineItems, RefundItem } from '@atlassian/cssxp-types-billing';
import {
  ColGroup,
  CopyableText,
  CustomDynamicResultTable,
  DSTLineItem,
  OpenSidebarButton,
  SearchResultColumn,
  TableBody,
  TableHead,
  getEntitlementRouteFromLineItems,
  NoDataContentInTable,
  useFeatureToggle,
  AddOnLozenge,
  FormatCurrency,
} from '@css-xp/base';
import { Divider } from '@css-xp/base/divider';
import { getBilledForColumnData, CloudProductDetailView } from '@css-xp/base/entitlement-utils';
import { getChargeElement, isPaypalPaymentMethod } from '@css-xp/ppc-utils';
import { LINE_ITEMS_TABLE_HEADER_NAMES, ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { Adjustments, InvoicePaymentMethod, BillingFrequency } from '@css-xp/graphtypes/src/generated-types-new';
import { getTaxSummary } from '../../../invoices/src/components/invoice-amount-details';
import { getTaxRate } from '../../../invoices/src/components/invoice-line-items';
import { getRefundDstLineItemDetails, getTotalPartnerMarginAmount, adjustedAmount } from '../refund-page-utils';
import { SIDEBAR } from './refund-page';
import './refund-line-items.scss';

interface RefundAdditionalProps {
  entitlementBaseRoute?: string;
  isPaymentDetailsHidden?: boolean;
  isInternalMemoHidden?: boolean;
}

const getColumns = memoize(
  (shouldShowPartnerMarginColumn, isDesignIteration1Enabled, currency): SearchResultColumn[] => {
    if (isDesignIteration1Enabled) {
      const currencyCode = currency ? ` (${currency})` : '';
      const columns = [
        {
          name: LINE_ITEMS_TABLE_HEADER_NAMES.DETAILS,
          customClassNames: 'col-30',
        },
        {
          name: LINE_ITEMS_TABLE_HEADER_NAMES.TAX_RATE,
          customClassNames: 'col-10',
          renderComponent: (
            // Added .currency-code-header for right text alignment
            <th className="currency-code-header">{`${LINE_ITEMS_TABLE_HEADER_NAMES.TAX_RATE}`}</th>
          ),
        },
        {
          name: LINE_ITEMS_TABLE_HEADER_NAMES.LIST_PRICE,
          customClassNames: 'col-15',
          renderComponent: (
            <th className="currency-code-header">{`${LINE_ITEMS_TABLE_HEADER_NAMES.LIST_PRICE}${currencyCode}`}</th>
          ),
        },
        ...(shouldShowPartnerMarginColumn
          ? [
              {
                name: LINE_ITEMS_TABLE_HEADER_NAMES.PARTNER_MARGIN,
                customClassNames: 'col-15',
                renderComponent: (
                  <th className="currency-code-header">
                    {`${LINE_ITEMS_TABLE_HEADER_NAMES.PARTNER_MARGIN}${currencyCode}`}
                  </th>
                ),
              },
            ]
          : []),
        {
          name: LINE_ITEMS_TABLE_HEADER_NAMES.DISCOUNTS_APPLIED,
          customClassNames: 'col-15',
          renderComponent: (
            <th className="currency-code-header">{`${LINE_ITEMS_TABLE_HEADER_NAMES.DISCOUNTS_APPLIED}${currencyCode}`}</th>
          ),
        },
        {
          name: LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_EXCL_TAX,
          customClassNames: 'col-15',
          renderComponent: (
            <th className="currency-code-header">
              {`${LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_EXCL_TAX}${currencyCode}`}
            </th>
          ),
        },
        {
          name: LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_INCL_TAX,
          customClassNames: 'col-15',
          renderComponent: (
            <th className="currency-code-header">
              {`${LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_INCL_TAX}${currencyCode}`}
            </th>
          ),
        },
      ];

      return columns;
    }

    let columns = [];
    columns = [
      {
        name: LINE_ITEMS_TABLE_HEADER_NAMES.DETAILS,
        customClassNames: 'col-30',
      },
      {
        name: LINE_ITEMS_TABLE_HEADER_NAMES.TAX_RATE,
        customClassNames: 'col-10',
      },
      {
        name: LINE_ITEMS_TABLE_HEADER_NAMES.USAGE,
        customClassNames: 'col-10',
      },
      {
        name: LINE_ITEMS_TABLE_HEADER_NAMES.LIST_PRICE,
        customClassNames: 'col-10',
      },
      ...(shouldShowPartnerMarginColumn
        ? [
            {
              name: LINE_ITEMS_TABLE_HEADER_NAMES.PARTNER_MARGIN,
              customClassNames: 'col-10',
            },
          ]
        : []),
      {
        name: LINE_ITEMS_TABLE_HEADER_NAMES.DISCOUNTS_APPLIED,
        customClassNames: 'col-10',
      },
      {
        name: LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_EXCL_TAX,
        customClassNames: 'col-10',
      },
      {
        name: LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_INCL_TAX,
        customClassNames: 'col-10',
      },
    ];

    return columns;
  }
);

export const emptyStateProps = {
  header: '',
  description: 'No refunds found',
  imageUrl: '',
};

const RefundLineItemDescription = ({
  item,
  entitlementBaseRoute,
}: {
  item: RefundItem;
  entitlementBaseRoute?: string;
}) => {
  return (
    <div>
      <CloudProductDetailView
        logoId={item?.logoId as string}
        productId={item?.productId as string}
        entitlementNumber={item.entitlementNumber}
        productName={item?.productName}
        productLink={
          entitlementBaseRoute
            ? entitlementBaseRoute + item?.entitlementId
            : //@ts-ignore
              getEntitlementRouteFromLineItems(item)
        }
        version={2}
      />
      {/* Classnames are same as present in invoice-line-items code. CSS is applied from invoice-line-items.scss. */}
      <div className="invoice-product-details pt-2" data-testId="product-edition-plan">
        {item?.entitlementId && <CopyableText text={item.entitlementId} />}
        {item.editionWhileRefund}{' '}
        {item?.pricingPlan?.billingFrequency && `| ${capitalize(item.pricingPlan.billingFrequency as string)}`}{' '}
        {item?.pricingPlan?.type && `| ${capitalize(item.pricingPlan.type)}`}
      </div>
      <div className="invoice-product-details" data-testId="product-usage">
        Usage: {item.quantity ?? 0}{' '}
        {getChargeElement({
          productId: item?.productId,
          labelPlural: Number(item?.quantity) !== 1,
          edition: item?.editionWhileRefund,
        })}
      </div>
    </div>
  );
};

export const StatelessInvoiceLineItems = ({
  refund,
  entitlementBaseRoute,
  isPaymentDetailsHidden = false,
  isInternalMemoHidden = false,
  shouldGroupAddOns = false,
  isRefundSummaryHidden = false,
}: {
  refund: Refund;
  entitlementBaseRoute?: string;
  isPaymentDetailsHidden?: boolean;
  isInternalMemoHidden?: boolean;
  shouldGroupAddOns?: boolean;
  isRefundSummaryHidden?: boolean;
}) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isPaypalSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);

  const getLineItemKey = (item: RefundItem) => {
    const identifier = item.id;
    const description = item.description;
    const amount = item.totalExTax;
    return `${identifier}-${description}-${amount}`;
  };

  const reason = refund.reason as string;
  const reasonCode = refund.reasonCode as string;

  const totalAmountPaid = refund.total as string;
  const isInvoice = false;
  const coupledRefundLineItems = refund.coupledRefundItems;

  const totalMargin = (refund.refundItems as RefundItem[]).reduce(
    (total: number, lineItem: RefundItem) => total + getTotalPartnerMarginAmount(get(lineItem, 'margins', [])),
    0
  );

  const marginAdjustedSubtotal = (toNumber(refund.totalExTax) - totalMargin).toFixed(2);
  const totalAmountExcludingTax = marginAdjustedSubtotal as string;

  const shouldShowPartnerMarginColumn = refund.refundItems?.some((lineItem: RefundItem) => {
    return (
      // lineItem?.billingCycle is in upperFirst case so wont match directly for refund
      lineItem?.billingCycle?.toUpperCase() === BillingFrequency.ANNUAL ||
      lineItem?.pricingPlan?.billingFrequency?.toUpperCase() === BillingFrequency.ANNUAL
    );
  });

  const totalAccountBalanceRefund = [...(refund?.refundItems || []), ...(refund?.dstRefundItems || [])].reduce(
    function (previousValue: number, refundItem: RefundItem) {
      if (refundItem?.refundToCustomerBalance) {
        return (previousValue += refundItem.refundToCustomerBalance);
      } else {
        return previousValue;
      }
    },
    0
  );

  const amountRefundedToCreditCard =
    toNumber(totalAmountExcludingTax) + toNumber(refund?.totalTax ?? 0) - toNumber(totalAccountBalanceRefund);

  const getAdjustmentsTotal = (adjustments: Adjustments[] | null | undefined) => {
    if (!adjustments) return 0;
    return adjustments.reduce((amount, a) => amount + (a.amount as number), 0);
  };

  const getDSTLineItemRow = (refund: Refund, columns: SearchResultColumn[]) => {
    if (!refund?.dstRefundItems?.length) return null;
    const { totalExTax, totalIncTax, dstTaxItems } = getRefundDstLineItemDetails(refund?.dstRefundItems);
    const uniqTaxItems = uniqBy(dstTaxItems, 'taxAmountLabel');
    const allTaxRate = getTaxRate(uniqTaxItems);
    return (
      <DSTLineItem
        totalExTax={totalExTax}
        totalIncTax={totalIncTax}
        taxRate={allTaxRate}
        currency={refund.currency}
        tableHeaders={columns}
      />
    );
  };

  const getLineItemRow = (item: RefundItem | any, currency: string) => {
    const taxRate = getTaxRate(item?.taxItems);
    const partnerMarginAmount = getTotalPartnerMarginAmount(get(item, 'margins', []));
    const renderViewPlanSidebar = () => {
      return (
        <OpenSidebarButton
          sidebar={SIDEBAR.VIEW_PRICING_DETAILS}
          sidebarOptions={
            isDesignIteration1Enabled
              ? {
                  sidebarTitle: 'Pricing',
                }
              : {
                  sidebarWidth: '968px',
                  isOverlay: true,
                }
          }
          payload={{
            entitlementId: item.entitlementId,
            entitlementNumber: item.entitlementNumber,
            billingFrequency: item.pricingPlan?.billingFrequency,
            productId: item.productId,
            productName: item.productName,
            offeringName: item.edition,
            pricingPlan: item.pricingPlan,
            activeUsers: item.quantity,
            isInvoicePage: true,
            logoId: item.logoId,
            ...(isDesignIteration1Enabled && { version: 2 }),
          }}
          appearance="link"
          testId="view-price-plan"
          label=" View plan"
        >
          View plan
        </OpenSidebarButton>
      );
    };

    const renderViewDiscountsSidebar = () => {
      return (
        <OpenSidebarButton
          sidebar={SIDEBAR.REFUND_VIEW_ADJUSTMENTS}
          sidebarOptions={
            isDesignIteration1Enabled
              ? {
                  sidebarTitle: item.entitlementNumber,
                }
              : {
                  sidebarWidth: '968px',
                  isOverlay: true,
                }
          }
          payload={{
            adjustments: item.adjustments,
            entitlementNumber: item.entitlementNumber,
            currency: currency,
            adjustmentDetails: item.adjustmentDetails,
            promotions: item.promotions,
          }}
          appearance="link"
          label="View discounts"
        >
          View discounts
        </OpenSidebarButton>
      );
    };

    const renderEntitlementDetails = (item: RefundItem) => {
      if (isDesignIteration1Enabled) {
        return <RefundLineItemDescription item={item} entitlementBaseRoute={entitlementBaseRoute} />;
      }

      return (
        <>
          {item.entitlementNumber && (
            <Link
              to={
                entitlementBaseRoute
                  ? entitlementBaseRoute + item?.entitlementId
                  : //@ts-ignore
                    getEntitlementRouteFromLineItems(item)
              }
            >
              <span>
                <strong>{item?.entitlementNumber}</strong>
              </span>
            </Link>
          )}
          {
            <div>
              <CopyableText text={item?.entitlementId} />
            </div>
          }
          <div>
            <>
              {item?.editionWhileRefund && item?.editionWhileRefund.includes(item.productName as string)
                ? ''
                : item.productName + ' '}
              {item?.editionWhileRefund && item?.pricingPlan
                ? item?.editionWhileRefund +
                  ' ' +
                  capitalize(item?.pricingPlan?.billingFrequency as string) +
                  ' ' +
                  item?.pricingPlan?.type
                : ''}
            </>
          </div>
          {/** @ts-ignore */}
          {item?.isAddOn && <AddOnLozenge />}
        </>
      );
    };

    return (
      <>
        {/* Entitlement Details */}
        <td>{renderEntitlementDetails(item)}</td>
        {/* Tax Rate % */}
        <td data-testid="item-tax-rate">{item.taxRate}</td>
        {/* Usage */}
        {!isDesignIteration1Enabled && (
          <td data-testid="item-billied-for">
            <span>
              {item.quantity ?? 0} {getBilledForColumnData(item?.productId, item?.edition)}
            </span>
          </td>
        )}
        {/* List price */}
        <td data-testid="item-amount-cell">
          <>
            <FormatCurrency
              showCommas
              value={item.totalExTax as string}
              showCurrencyCode={!isDesignIteration1Enabled}
              currency={currency}
            />
            {item?.pricingPlanId && (
              <div className="view-price-plan-link">
                {isDesignIteration1Enabled ? (
                  renderViewPlanSidebar()
                ) : (
                  <Tooltip content="View plan" hideTooltipOnClick>
                    {renderViewPlanSidebar()}
                  </Tooltip>
                )}
              </div>
            )}
          </>
        </td>
        {/* Partner discount */}
        {shouldShowPartnerMarginColumn && (
          <td>
            <FormatCurrency
              showCommas
              value={partnerMarginAmount}
              currency={refund.currency}
              showCurrencyCode={!isDesignIteration1Enabled}
            />
          </td>
        )}
        {/* Discounts applied */}
        <td data-testid="adjustment-value" className="adjustment-value">
          {getAdjustmentsTotal(item.adjustments) > 0 ? (
            <>
              {item.adjustments?.map((discount: Adjustment, index: number) => (
                <div key={index}>
                  <FormatCurrency
                    showCommas
                    key={`${discount.amount}-${index}`}
                    value={discount.amount ? discount.amount : 0}
                    currency={currency}
                    showCurrencyCode={!isDesignIteration1Enabled}
                  />
                </div>
              ))}
              <div className="total-adjustments-link">
                {isDesignIteration1Enabled ? (
                  renderViewDiscountsSidebar()
                ) : (
                  <Tooltip content="View discounts" position="bottom" hideTooltipOnClick>
                    {renderViewDiscountsSidebar()}
                  </Tooltip>
                )}
              </div>
            </>
          ) : (
            <NoDataContentInTable testId="adjustment-value-no-data" />
          )}
        </td>
        {/* Amount excl. tax */}
        <td>
          <FormatCurrency
            showCommas
            value={adjustedAmount(item.totalExTax, item.adjustments, partnerMarginAmount)}
            currency={currency}
            showCurrencyCode={!isDesignIteration1Enabled}
          />
        </td>
        {/* Amount incl. tax */}
        <td>
          <FormatCurrency
            showCommas
            value={adjustedAmount(item.total, null, null)}
            currency={currency}
            showCurrencyCode={!isDesignIteration1Enabled}
          />
          {taxRate && (
            <span data-testid="tax-tooltip" className="tax-tooltip">
              <Tooltip content={taxRate} position="bottom-start" tag="span">
                <InfoIcon size="small" label="pending-invoice-info" />
              </Tooltip>
            </span>
          )}
        </td>
      </>
    );
  };

  const renderProductRow = (items: RefundItem[]) => {
    const rowClassForBackground = isDesignIteration1Enabled ? '' : ' grey-row';

    return items.map((lineItem: RefundItem) => {
      return (
        <tr key={getLineItemKey(lineItem)} className={'coupling-line-item' + rowClassForBackground}>
          {getLineItemRow(lineItem, refund.currency)}
        </tr>
      );
    });
  };

  const renderAppRow = (items: RefundItem[]) => {
    return items.map((lineItem: RefundItem) => {
      return (
        <tr key={getLineItemKey(lineItem)} className="coupling-line-item white-row">
          {getLineItemRow(lineItem, refund.currency)}
        </tr>
      );
    });
  };

  const columns = getColumns(shouldShowPartnerMarginColumn, isDesignIteration1Enabled, refund.currency);

  const renderInvoiceAmountDetails = () => {
    const renderTotalAmountExcludingTax = () => {
      return <FormatCurrency showCommas showCurrencyCode currency={refund.currency} value={totalAmountExcludingTax} />;
    };
    const renderTaxApplied = () => {
      return <FormatCurrency showCommas showCurrencyCode currency={refund.currency} value={refund.totalTax ?? 0} />;
    };
    const renderTaxSummary = (isVersionTwo?: boolean) => {
      return getTaxSummary({
        lineItems: [...refund.refundItems, ...(refund?.dstRefundItems || [])],
        paymentCurrency: refund.currency,
        isInvoice: isVersionTwo ? true : isInvoice,
        isVersionTwo,
      });
    };
    const renderRefundedPaymentDetails = () => {
      const refundedTextForCreditCard = isDesignIteration1Enabled
        ? `Refunded to credit card ****${refund.creditCardLastFourDigits}`
        : `Refunded to credit card <****${refund.creditCardLastFourDigits}>`;

      return (
        !isPaymentDetailsHidden && (
          <>
            {amountRefundedToCreditCard > 0 && (
              <div className="total-amount-excl-tax" data-testid="refund-card-details">
                <p>
                  {refund?.paymentMethod === InvoicePaymentMethod.DEFERRED ||
                  (isPaypalSupportEnabled && isPaypalPaymentMethod(refund.paymentMethod))
                    ? 'Refunded to chosen payment method'
                    : refundedTextForCreditCard}
                </p>

                <FormatCurrency
                  showCommas
                  showCurrencyCode
                  currency={refund.currency}
                  value={amountRefundedToCreditCard}
                />
              </div>
            )}
            <div className="total-amount-excl-tax" data-testid="refund-account-balance">
              <p>Refunded to account balance</p>
              <FormatCurrency
                showCommas
                showCurrencyCode
                currency={refund.currency}
                value={totalAccountBalanceRefund}
              />
            </div>
          </>
        )
      );
    };
    const renderTotalAmount = () => {
      return <FormatCurrency showCommas showCurrencyCode currency={refund.currency} value={totalAmountPaid} />;
    };
    const totalAmountText = 'Total amount excl. tax';
    const totalTaxText = 'Total tax applied';
    const totalText = 'Total';

    if (isDesignIteration1Enabled) {
      return (
        <div className="refund-amount-details" data-testid="refund-amount-details">
          <div>
            <Text weight="semibold">{totalAmountText}</Text>
            <Text weight="semibold">{renderTotalAmountExcludingTax()}</Text>
          </div>
          <div>
            <p>{totalTaxText}</p>
            <div>{renderTaxApplied()}</div>
          </div>
          {renderTaxSummary(true)}
          {renderRefundedPaymentDetails()}
          <Divider className="refund-amount-divider" />
          <div className="total-amount-paid">
            <p>{totalText}</p>
            {renderTotalAmount()}
          </div>
        </div>
      );
    }

    return (
      <div className="invoice-amount-details">
        <div className="floated-right">
          <div className="invoice-amounts-container" data-testid="invoice-amount-details">
            <div className="total-amount-excl-tax">
              <p>{totalAmountText}</p>
              {renderTotalAmountExcludingTax()}
            </div>
            <div className="total-tax">
              <p>{totalTaxText}</p>
              <div className="tax-value">{renderTaxApplied()}</div>
            </div>
            {renderRefundedPaymentDetails()}
            <div className="total-amount-paid">
              <p>{totalText}</p>
              {renderTotalAmount()}
            </div>
            {renderTaxSummary()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="invoice-line-items-container">
      {isDesignIteration1Enabled || <h4>Refunds</h4>}
      <div
        className={classNames('margin-top', {
          'line-items-payment-details-container': !isDesignIteration1Enabled,
          'line-items-payment-details-container-v2 mb-40 refund-items-container': isDesignIteration1Enabled,
        })}
      >
        <CustomDynamicResultTable
          emptyStateProps={emptyStateProps}
          noResults={refund.refundItems.length <= 0}
          isLoading={false}
          error={''}
          testId={'refund-line-items-table'}
          boxShadow={false}
        >
          <ColGroup columns={columns} />
          <TableHead columns={columns} />
          <TableBody>
            {!coupledRefundLineItems
              ? refund.refundItems.map((item: RefundItem) => (
                  <React.Fragment key={getLineItemKey(item)}>
                    <tr className="line-item">{getLineItemRow(item, refund.currency)}</tr>
                  </React.Fragment>
                ))
              : Object.keys(coupledRefundLineItems).map((key: string) => {
                  return (
                    <>
                      {
                        //@ts-ignore
                        coupledRefundLineItems[key]?.products?.length > 0 && (
                          <>
                            {
                              //@ts-ignore
                              renderProductRow(coupledRefundLineItems[key].products)
                            }
                          </>
                        )
                      }
                      {
                        //@ts-ignore
                        shouldGroupAddOns && coupledRefundLineItems[key]?.addOns?.length > 0 && (
                          <>
                            <div className="apps-heading">{`Add-ons`}</div>
                            {
                              //@ts-ignore
                              renderAppRow(coupledRefundLineItems[key].addOns)
                            }
                          </>
                        )
                      }
                      {
                        //@ts-ignore
                        coupledRefundLineItems[key]?.apps?.length > 0 && (
                          <>
                            <div className="apps-heading">{`Apps for ${capitalize(key)}`}</div>
                            {
                              //@ts-ignore
                              renderAppRow(coupledRefundLineItems[key].apps)
                            }
                          </>
                        )
                      }
                    </>
                  );
                })}
            {getDSTLineItemRow(refund, columns)}
          </TableBody>
        </CustomDynamicResultTable>
        {renderInvoiceAmountDetails()}
      </div>
      <div className="refund-additional-details">
        {!isRefundSummaryHidden && (
          <>
            <h4>Refund summary</h4>
            <div className="line-items-payment-details-container margin-top">
              <div className="custom-dynamic-result-table margin-0">
                <table className="aui refund-summary-items" data-test-id="refund-summary">
                  <colgroup>
                    <col className="description-column" />
                    <col className="reasons-column" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Reason</th>
                      {refund.sourceSystem === SourceSystem.HAMS && <th>Reason code</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{upperFirst(lowerCase(reason))}</td>
                      {refund.sourceSystem === SourceSystem.HAMS && <td>{upperFirst(lowerCase(reasonCode))}</td>}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        {!isInternalMemoHidden && refund.creationNotes && (
          <div className="refund-creation-notes" data-testid="refund-internal-memo">
            <h4>Internal memo</h4>
            <div className="line-items-payment-details-container table-container">
              <div className="custom-dynamic-result-table margin-0">
                <p>{refund.creationNotes}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const RefundLineItems = (billingDoc: BillingDocWithLineItems & RefundAdditionalProps) => {
  const { entitlementBaseRoute, isPaymentDetailsHidden, isInternalMemoHidden, ...rest } = billingDoc;
  const refund = rest as Refund;

  return (
    <StatelessInvoiceLineItems
      refund={refund}
      entitlementBaseRoute={entitlementBaseRoute}
      isPaymentDetailsHidden={isPaymentDetailsHidden}
      isInternalMemoHidden={isInternalMemoHidden}
    />
  );
};

export default RefundLineItems;
